import { FormattedMessage } from 'react-intl'
import { getDayNameAndDate } from 'helpers/dates'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import Link from 'components/shared/Link'
import { CityBreakTicket } from 'components/common/CityBreak/components/CityBreakBox/components/CityBreakTicket'
import { CityBreakTicketsProps } from './CityBreakTickets.types'
import useTicketTranslations from 'utils/hooks/useTicketTranslations'
import Loader from 'components/shared/Loader'
import { CityBreakTicketsContainer } from './CityBreakTickets.styles'

export const CityBreakTickets = ({
    title,
    datesRange,
    daysNumber,
    tickets,
    hasPromoTickets,
    isCtaLoading,
    isCtaDisabled,
    onDelete,
}: CityBreakTicketsProps) => {
    const { areTranslationsReady } = useTicketTranslations()

    const startDate = datesRange[0]
    const endDate = datesRange[datesRange.length - 1]
    const startDateNamed = `${getDayNameAndDate({ date: startDate })}`
    const endDateNamed = `${getDayNameAndDate({ date: endDate })}`

    // bigger means: title + cta button
    const hasBiggerHeader = !!onDelete

    return (
        <CityBreakTicketsContainer
            gap={hasBiggerHeader ? 24 : [16, 24]}
            isFullWidth
        >
            {(!!title || !!onDelete) && (
                <Box
                    direction="row"
                    justify="space-between"
                    align="center"
                    isFullWidth
                >
                    {!!title && (
                        <Box
                            width={hasBiggerHeader ? [160, 'auto'] : undefined}
                        >
                            <Text
                                variant={
                                    hasBiggerHeader ? 'heading3' : 'heading4'
                                }
                                color="grayscale_900"
                            >
                                {title}
                            </Text>
                        </Box>
                    )}
                    {!!onDelete && (
                        <>
                            {isCtaLoading ? (
                                <Loader />
                            ) : (
                                <Link
                                    variant="primary"
                                    textVariant="body16Strong"
                                    icon={null}
                                    onClick={onDelete}
                                    data-testid="city-button-delete"
                                    isDisabled={isCtaDisabled}
                                >
                                    <FormattedMessage
                                        id="global.btn.remove_2"
                                        defaultMessage="_"
                                    />
                                </Link>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* dates */}
            <Box direction="row" align="center" gap={8} isFullWidth>
                <Text variant="heading5" color="grayscale_900">
                    {startDateNamed}{' '}
                    {datesRange.length > 1 && `- ${endDateNamed}`}
                </Text>
                {daysNumber > 1 && (
                    <Text variant="body14" color="grayscale_800">
                        (
                        <FormattedMessage
                            id="global.plurals.days"
                            defaultMessage="_"
                            values={{
                                days: daysNumber,
                            }}
                        />
                        )
                    </Text>
                )}
            </Box>

            {/* promotional tickets - offer */}
            <Box direction="column" gap={16} mt={16} isFullWidth>
                {!areTranslationsReady ? (
                    <Loader />
                ) : (
                    tickets.map((ticket, index) => {
                        const { quantity } = ticket

                        return quantity > 0 ? (
                            <CityBreakTicket
                                ticketData={ticket}
                                daysNumber={daysNumber}
                                isPromoTicket={hasPromoTickets}
                                key={index}
                            />
                        ) : null
                    })
                )}
            </Box>
        </CityBreakTicketsContainer>
    )
}
